/**
 * Bale WebApp SDK Service
 * Provides a wrapper around the Bale WebApp SDK for mini app functionality
 */

// Type definitions for Bale WebApp SDK
interface BaleWebAppMainButton {
  text: string;
  color: string;
  textColor: string;
  isVisible: boolean;
  isActive: boolean;
  isProgressVisible: boolean;
  setText: (text: string) => void;
  onClick: (callback: () => void) => void;
  offClick: (callback: () => void) => void;
  show: () => void;
  hide: () => void;
  enable: () => void;
  disable: () => void;
  showProgress: (leaveActive?: boolean) => void;
  hideProgress: () => void;
  setParams: (params: Partial<BaleWebAppMainButton>) => void;
}

interface BaleWebAppThemeParams {
  bg_color?: string;
  text_color?: string;
  hint_color?: string;
  link_color?: string;
  button_color?: string;
  button_text_color?: string;
  secondary_bg_color?: string;
}

interface BaleWebAppInitDataUnsafe {
  query_id?: string;
  user?: {
    id: number;
    first_name: string;
    last_name?: string;
    username?: string;
    language_code?: string;
    is_premium?: boolean;
  };
  receiver?: {
    id: number;
    first_name: string;
    last_name?: string;
    username?: string;
  };
  chat?: {
    id: number;
    type: string;
    title?: string;
    username?: string;
  };
  start_param?: string;
  auth_date?: number;
  hash?: string;
}

interface BaleWebApp {
  initData: string;
  initDataUnsafe: BaleWebAppInitDataUnsafe;
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  themeParams: BaleWebAppThemeParams;
  isExpanded: boolean;
  viewportHeight: number;
  viewportStableHeight: number;
  headerColor: string;
  backgroundColor: string;
  isClosingConfirmationEnabled: boolean;
  MainButton: BaleWebAppMainButton;
  ready: () => void;
  expand: () => void;
  close: () => void;
  enableClosingConfirmation: () => void;
  disableClosingConfirmation: () => void;
  onEvent: (eventType: string, callback: () => void) => void;
  offEvent: (eventType: string, callback: () => void) => void;
  sendData: (data: string) => void;
  openLink: (url: string, options?: { try_instant_view?: boolean }) => void;
  openTelegramLink: (url: string) => void;
  openInvoice: (url: string, callback?: (status: string) => void) => void;
  showPopup: (params: { title?: string; message: string; buttons?: Array<{ id?: string; type?: string; text: string }> }, callback?: (buttonId: string) => void) => void;
  showAlert: (message: string, callback?: () => void) => void;
  showConfirm: (message: string, callback?: (confirmed: boolean) => void) => void;
  showScanQrPopup: (params: { text?: string }, callback?: (data: string) => boolean) => void;
  closeScanQrPopup: () => void;
  readTextFromClipboard: (callback?: (text: string) => void) => void;
  requestWriteAccess: (callback?: (granted: boolean) => void) => void;
  requestContact: (callback?: (granted: boolean, contact?: { phone_number?: string; first_name?: string; last_name?: string }) => void) => void;
  setHeaderColor: (color: string) => void;
  setBackgroundColor: (color: string) => void;
}

declare global {
  interface Window {
    Bale?: {
      WebApp: BaleWebApp;
    };
    Telegram?: {
      WebApp: BaleWebApp;
    };
  }
}

/**
 * Service class for interacting with Bale WebApp SDK
 */
class BaleWebAppService {
  private webApp: BaleWebApp | null = null;
  private isInitialized: boolean = false;

  /**
   * Initialize the Bale WebApp SDK
   * Should be called once when the app starts
   */
  initialize(): void {
    if (this.isInitialized) {
      return;
    }

    const webApp = window.Bale?.WebApp ?? window.Telegram?.WebApp;

    if (webApp) {
      this.webApp = webApp;
      this.webApp.ready();
      this.webApp.expand();
      this.isInitialized = true;
      console.log('Bale WebApp SDK initialized', {
        version: this.webApp.version,
        platform: this.webApp.platform,
      });
    } else {
      console.warn('Bale WebApp SDK not available - running in development mode');
    }
  }

  /**
   * Get the InitData string for authentication
   * In development mode, returns a mock value
   */
  getInitData(): string {
    if (!this.webApp) {
      return '';
    }
    return this.webApp.initData;
  }

  /**
   * Get the start parameter from deep link
   * Format: view=balance or view=plans&plan_id=123
   */
  getStartParam(): string | null {
    if (!this.webApp) {
      return null;
    }
    return this.webApp.initDataUnsafe?.start_param || null;
  }

  /**
   * Get user information from InitData
   */
  getUserInfo(): BaleWebAppInitDataUnsafe['user'] | null {
    if (!this.webApp) {
      return null;
    }
    return this.webApp.initDataUnsafe?.user || null;
  }

  /**
   * Close the mini app
   */
  close(): void {
    this.webApp?.close();
  }

  /**
   * Set the header color
   */
  setHeaderColor(color: string): void {
    this.webApp?.setHeaderColor(color);
  }

  /**
   * Set the background color
   */
  setBackgroundColor(color: string): void {
    this.webApp?.setBackgroundColor(color);
  }

  /**
   * Show the main button with text
   */
  showMainButton(text: string, onClick: () => void): void {
    if (!this.webApp) {
      return;
    }
    this.webApp.MainButton.setText(text);
    this.webApp.MainButton.onClick(onClick);
    this.webApp.MainButton.show();
  }

  /**
   * Hide the main button
   */
  hideMainButton(): void {
    this.webApp?.MainButton.hide();
  }

  /**
   * Show an alert dialog
   */
  showAlert(message: string, callback?: () => void): void {
    if (!this.webApp) {
      alert(message);
      callback?.();
      return;
    }
    this.webApp.showAlert(message, callback);
  }

  /**
   * Show a confirmation dialog
   */
  showConfirm(message: string, callback?: (confirmed: boolean) => void): void {
    if (!this.webApp) {
      const confirmed = confirm(message);
      callback?.(confirmed);
      return;
    }
    this.webApp.showConfirm(message, callback);
  }

  /**
   * Open a URL in the browser
   */
  openLink(url: string): void {
    if (!this.webApp) {
      window.open(url, '_blank');
      return;
    }
    this.webApp.openLink(url);
  }

  /**
   * Check if the SDK is available
   */
  isAvailable(): boolean {
    return this.webApp !== null;
  }

  /**
   * Get the current theme (light or dark)
   */
  getColorScheme(): 'light' | 'dark' {
    return this.webApp?.colorScheme || 'light';
  }

  /**
   * Get theme colors
   */
  getThemeParams(): BaleWebAppThemeParams {
    return this.webApp?.themeParams || {};
  }

  /**
   * Enable closing confirmation
   */
  enableClosingConfirmation(): void {
    this.webApp?.enableClosingConfirmation();
  }

  /**
   * Disable closing confirmation
   */
  disableClosingConfirmation(): void {
    this.webApp?.disableClosingConfirmation();
  }
}

// Create and export a singleton instance
export const baleWebApp = new BaleWebAppService();
